import { StatCard, ProgressBar } from '@/components/shared/StatCard'
import type { EntityDashboardStats } from '@/hooks/useDashboardData'
import { cn } from '@/lib/utils'

interface EntityStatsProps {
  title: string
  stats: EntityDashboardStats
}

const bucketLabels = [
  { key: 'high' as const, label: '0.90+', level: 'high' },
  { key: 'medium' as const, label: '0.75\u20130.89', level: 'medium' },
  { key: 'low' as const, label: '<0.75', level: 'low' },
  { key: 'unmatched' as const, label: 'Unmatched', level: 'none' },
] as const

const badgeColors: Record<string, string> = {
  high: 'bg-[hsl(var(--conf-high))] text-white',
  medium: 'bg-[hsl(var(--conf-med))] text-white',
  low: 'bg-[hsl(var(--conf-low))] text-white',
  none: 'bg-[hsl(var(--conf-none))] text-white',
}

export function EntityStats({ title, stats }: EntityStatsProps) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{title}</h2>

      <StatCard title="Matched">
        <ProgressBar matched={stats.matched} total={stats.total} />
      </StatCard>

      <StatCard title="Flagged / Verified">
        <div className="flex items-baseline gap-3 text-2xl font-bold tracking-tight">
          <span className="text-[hsl(var(--conf-low))]">{stats.flagged}</span>
          <span className="text-base font-normal text-muted-foreground">/</span>
          <span className="text-[hsl(var(--conf-high))]">{stats.verified}</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Needing review / already reviewed</p>
      </StatCard>

      <div>
        <h3 className="mb-2 text-sm font-medium text-muted-foreground">Confidence Distribution</h3>
        <div className="space-y-1.5">
          {bucketLabels.map(({ key, label, level }) => (
            <div key={key} className="flex items-center gap-2">
              <span
                className={cn(
                  'inline-flex min-w-[5.5rem] items-center justify-center rounded px-2 py-0.5 text-xs font-medium',
                  badgeColors[level],
                )}
              >
                {label}
              </span>
              <span className="text-sm">
                {stats.confidence[key]} {title.toLowerCase()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
